// RoofEstimator — customer-facing card that hits Google Solar API for an
// instant roof size + cost range. Used on:
//   - Public booking widget (Roofing accounts only) — no auth required
//   - Tech portal / desktop Jobs modal — post-address estimate for the tech
//
// When a paid provider (EagleView / Hover / RoofSnap) is configured on the
// account, we show "Escalate to [provider]" buttons for the operator side.
// The public booking widget only ever calls the free Google endpoint.

import React, { useState } from 'react';
import { G } from '../theme.js';

export function RoofEstimator({ address: initialAddress, mode = 'public', theme = 'auto' }) {
  const [address, setAddress] = useState(initialAddress || '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const T = theme === 'light' ? LIGHT : {
    card: G.card, border: G.border, text: G.text, muted: G.textMuted,
    accent: G.purple, accentDim: G.purpleDim, success: G.green, bg: G.card,
  };

  const measure = async () => {
    if (!address.trim()) { setErr('Enter an address first.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await fetch('/api/roof/estimate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ address: address.trim() }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'Estimate failed');
      setResult(j);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
    <div style={{ fontSize: 12, color: T.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>🛰 Roof Estimator</div>
    <div style={{ fontSize: 13, color: T.muted, marginBottom: 12, lineHeight: 1.5 }}>
      Get an instant roof size + cost estimate from satellite imagery.
    </div>
    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
      <input value={address} onChange={e => setAddress(e.target.value)}
        placeholder="123 Main St, Tampa FL 33601"
        style={{ flex: 1, padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 8, background: T.bg, color: T.text, fontSize: 14 }}/>
      <button onClick={measure} disabled={busy}
        style={{ padding: '10px 16px', background: T.accent, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
        {busy ? '…' : 'Measure'}
      </button>
    </div>
    {err && <div style={{ color: '#c92026', fontSize: 13, padding: '8px 10px', background: '#fee', borderRadius: 6 }}>{err}</div>}
    {result && <ResultCard r={result} T={T} mode={mode} address={address}/>}
  </div>;
}

function ResultCard({ r, T, mode, address }) {
  return <div style={{ marginTop: 12, padding: 14, background: T.accentDim, border: `1px solid ${T.accent}44`, borderRadius: 10 }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 12 }}>
      <Stat T={T} label="Total roof area"        value={`${(r.roof.areaSqFt || 0).toLocaleString()} sq ft`}/>
      <Stat T={T} label="Average pitch"          value={`${r.roof.pitchDegrees}°`}/>
      <Stat T={T} label="Roof planes"            value={r.roof.planeCount}/>
    </div>
    {r.estimateRange && (
      <div style={{ padding: 12, background: T.card, borderRadius: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>Estimated replacement</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: T.text, marginTop: 2 }}>
          ${(r.estimateRange.low || 0).toLocaleString()} – ${(r.estimateRange.high || 0).toLocaleString()}
        </div>
        <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>{r.estimateRange.basis}. Actual quote depends on materials + tear-off + penetrations.</div>
      </div>
    )}
    {r.roof.planes?.length > 0 && (
      <details style={{ fontSize: 12, color: T.muted, marginTop: 8 }}>
        <summary style={{ cursor: 'pointer' }}>Per-plane breakdown ({r.roof.planes.length})</summary>
        <div style={{ marginTop: 8, display: 'grid', gap: 4 }}>
          {r.roof.planes.map((p, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, fontSize: 12 }}>
              <span>Plane {i + 1}: {p.areaSqFt} sq ft</span>
              <span>Pitch {p.pitchDegrees}°</span>
              <span>Azimuth {p.azimuthDegrees}°</span>
            </div>
          ))}
        </div>
      </details>
    )}
    {mode !== 'public' && <PremiumEscalation address={address} T={T}/>}
    <div style={{ marginTop: 10, fontSize: 10, color: T.muted, textAlign: 'right' }}>
      Data: {r.provider === 'google_solar' ? 'Google Solar API' : r.provider}
    </div>
  </div>;
}

function Stat({ T, label, value }) {
  return <div style={{ background: T.card, padding: '8px 10px', borderRadius: 6 }}>
    <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 800, color: T.text, marginTop: 2 }}>{value}</div>
  </div>;
}

// Only shown to authenticated operators (mode !== 'public'). Fetches which
// paid providers this account has API keys for and offers "escalate" buttons.
function PremiumEscalation({ address, T }) {
  const [providers, setProviders] = React.useState(null);
  const [ordering, setOrdering] = React.useState(null);
  const [ordered, setOrdered] = React.useState({});
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    fetch('/api/roof/providers', { credentials: 'include' })
      .then(r => r.json()).then(setProviders).catch(() => setProviders({}));
  }, []);
  if (!providers) return null;
  const anyConnected = providers.eagleview || providers.hover || providers.roofsnap;
  const order = async (provider) => {
    setOrdering(provider); setErr(null);
    try {
      const r = await fetch('/api/roof/estimate/premium', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address, provider }),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'Order failed');
      setOrdered({ ...ordered, [provider]: j });
    } catch (e) { setErr(e.message); }
    finally { setOrdering(null); }
  };
  return <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
    <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>Need insurance-grade?</div>
    {!anyConnected ? (
      <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.5 }}>
        Connect EagleView, Hover, or RoofSnap in <strong>Settings → Integrations</strong> to escalate this estimate to a paid, human-verified report.
      </div>
    ) : (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {providers.eagleview && <PremiumBtn T={T} name="EagleView"  provider="eagleview" busy={ordering==='eagleview'} onClick={() => order('eagleview')} ordered={ordered.eagleview}/>}
        {providers.hover     && <PremiumBtn T={T} name="Hover"      provider="hover"     busy={ordering==='hover'}     onClick={() => order('hover')}     ordered={ordered.hover}/>}
        {providers.roofsnap  && <PremiumBtn T={T} name="RoofSnap"   provider="roofsnap"  busy={ordering==='roofsnap'}  onClick={() => order('roofsnap')}  ordered={ordered.roofsnap}/>}
      </div>
    )}
    {err && <div style={{ color: '#c92026', fontSize: 12, marginTop: 6 }}>{err}</div>}
  </div>;
}

function PremiumBtn({ T, name, busy, onClick, ordered }) {
  if (ordered) return <div style={{ padding: '6px 12px', background: T.success + '22', color: T.success, border: `1px solid ${T.success}66`, borderRadius: 8, fontSize: 12, fontWeight: 700 }}>✓ {name} ordered</div>;
  return <button onClick={onClick} disabled={busy}
    style={{ padding: '6px 12px', background: 'transparent', color: T.accent, border: `1px solid ${T.accent}66`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
    {busy ? '…' : `Order ${name}`}
  </button>;
}

const LIGHT = {
  bg: '#fff', card: '#fff', border: '#e4e6ec', text: '#111827', muted: '#5b6478',
  accent: '#0f6dff', accentDim: '#e6efff', success: '#137e5f',
};
